import { LeagueTier, isBottomTier } from '../value-objects/LeagueTier';
import { PromotionStatus, calculatePromotionStatus } from '../value-objects/PromotionStatus';

export interface StudentRankingProps {
  id: string;
  studentId: string;
  studentName: string;
  studentAvatar?: string;
  leagueId: string;
  tier: LeagueTier;
  weekXP: number;
  totalXP: number;
  lifetimeXP: number; // XP permanente acumulado de por vida
  inactiveCycles: number; // Ciclos consecutivos sin participar
  position: number;
  previousPosition?: number;
  currentStreak: number;
  longestStreak: number;
  lessonsCompleted: number;
  quizzesPassed: number;
  videosWatched: number;
  weeklyCycleId: string;
  weekStartDate: Date;
  weekEndDate: Date;
  updatedAt: Date;
}

export class StudentRanking {
  private readonly props: StudentRankingProps;

  constructor(props: StudentRankingProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get studentId(): string {
    return this.props.studentId;
  }

  get studentName(): string {
    return this.props.studentName;
  }

  get studentAvatar(): string | undefined {
    return this.props.studentAvatar;
  }

  get leagueId(): string {
    return this.props.leagueId;
  }

  get tier(): LeagueTier {
    return this.props.tier;
  }

  get weekXP(): number {
    return this.props.weekXP;
  }

  get totalXP(): number {
    return this.props.totalXP;
  }

  get lifetimeXP(): number {
    return this.props.lifetimeXP;
  }

  get inactiveCycles(): number {
    return this.props.inactiveCycles;
  }

  get position(): number {
    return this.props.position;
  }

  get previousPosition(): number | undefined {
    return this.props.previousPosition;
  }

  get currentStreak(): number {
    return this.props.currentStreak;
  }

  get longestStreak(): number {
    return this.props.longestStreak;
  }

  get lessonsCompleted(): number {
    return this.props.lessonsCompleted;
  }

  get quizzesPassed(): number {
    return this.props.quizzesPassed;
  }

  get videosWatched(): number {
    return this.props.videosWatched;
  }

  get weekStartDate(): Date {
    return this.props.weekStartDate;
  }

  get weekEndDate(): Date {
    return this.props.weekEndDate;
  }

  /**
   * Calculate promotion status based on position and total members
   */
  getPromotionStatus(totalMembers: number): PromotionStatus {
    return calculatePromotionStatus(
      this.props.position,
      totalMembers,
      isBottomTier(this.props.tier)
    );
  }

  /**
   * Calculate position change since last update
   * Positive = moved up, Negative = moved down
   */
  getPositionChange(): number {
    if (!this.props.previousPosition) return 0;
    return this.props.previousPosition - this.props.position;
  }

  /**
   * Check if position improved
   */
  hasImproved(): boolean {
    return this.getPositionChange() > 0;
  }

  /**
   * Check if position worsened
   */
  hasWorsened(): boolean {
    return this.getPositionChange() < 0;
  }

  /**
   * Check if in top 3
   */
  isTopThree(): boolean {
    return this.props.position <= 3;
  }

  /**
   * Get medal emoji for top 3
   */
  getMedal(): string | null {
    if (this.props.position === 1) return '🥇';
    if (this.props.position === 2) return '🥈';
    if (this.props.position === 3) return '🥉';
    return null;
  }

  /**
   * Check if has active streak (at least 2 days)
   */
  hasActiveStreak(): boolean {
    return this.props.currentStreak >= 2;
  }

  /**
   * Check if this is a personal best streak
   */
  isStreakPersonalBest(): boolean {
    return (
      this.props.currentStreak === this.props.longestStreak &&
      this.props.currentStreak > 0
    );
  }

  /**
   * Get total activities completed this week
   */
  getTotalActivities(): number {
    return this.props.lessonsCompleted + this.props.quizzesPassed + this.props.videosWatched;
  }

  /**
   * Get remaining time in the week
   */
  getRemainingTime(): number {
    const now = new Date();
    if (now > this.props.weekEndDate) return 0;
    return this.props.weekEndDate.getTime() - now.getTime();
  }

  /**
   * Get remaining days in the week
   */
  getRemainingDays(): number {
    return Math.ceil(this.getRemainingTime() / (1000 * 60 * 60 * 24));
  }

  toPlainObject(): StudentRankingProps {
    return { ...this.props };
  }
}
